/**
 * Reverse map landmark links for the active Database entry (#172).
 * Kept out of EntityLinksPanel.vue so that SFC can keep a single useTea loop
 * without local `ref` state for the async map scan.
 */
import { computed, ref, watch, type Ref } from "vue";
import { fromMaybe, isNothing, type Maybe } from "../lib/maybe";
import {
  findMapLandmarkRefs,
  type MapLandmarkRef,
} from "../lib/mapLandmarkLinks";
import { parseMapDocument } from "../lib/mapDocument";
import { joinProjectPath } from "../lib/runtime";
import { readProjectFile } from "../lib/tauri/core";
import { PROJECT_DATABASE_DIR } from "../lib/zwConfig";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";

export function useEntityMapRefs(): {
  refs: Ref<MapLandmarkRef[]>;
  loading: Ref<boolean>;
  hasRefs: Ref<boolean>;
} {
  const session = useEditorSessionStore();
  const outlineEntities = useOutlineEntitiesStore();

  const refs = ref<MapLandmarkRef[]>([]);
  const loading = ref(false);
  let loadToken = 0;

  const activeFile = computed((): Maybe<string> => session.activeDatabase);

  const mapNames = computed(() =>
    outlineEntities.databaseEntryFields
      .filter((entry) => entry.isMap === true)
      .map((entry) => entry.name),
  );

  const hasRefs = computed(() => refs.value.length > 0);

  watch(
    () => ({
      file: activeFile.value,
      mapsKey: mapNames.value.join("\0"),
      root: fromMaybe(session.rootPath),
    }),
    async ({ file, root }) => {
      const token = ++loadToken;
      refs.value = [];
      if (isNothing(file)) return;
      if (root === null || root.length === 0) return;
      const maps = mapNames.value;
      if (maps.length === 0) return;
      loading.value = true;
      try {
        const loaded = await Promise.all(
          maps.map(async (name) => {
            try {
              const raw = await readProjectFile(
                joinProjectPath(root, `${PROJECT_DATABASE_DIR}/${name}`),
              );
              return { name, doc: parseMapDocument(raw) };
            } catch {
              return null;
            }
          }),
        );
        if (token !== loadToken) return;
        const mapsOk = loaded.filter(
          (row): row is NonNullable<typeof row> => row !== null,
        );
        refs.value = findMapLandmarkRefs(file.value, mapsOk);
      } finally {
        if (token === loadToken) loading.value = false;
      }
    },
    { immediate: true },
  );

  return { refs, loading, hasRefs };
}
